import { useCallback, useEffect, useRef, useState } from 'react';
import type { FeedbackProblemItem } from '@components/feedbackProblemPicker';
import type { FeedbackProductItem } from '@components/feedbackProductPicker';

const getProblemId = (problem: FeedbackProblemItem | null | undefined) => (
    String(problem?.value ?? problem?.id ?? '')
);

const getProblemName = (problem: FeedbackProblemItem | null | undefined) => (
    problem?.name || ''
);

const getProductId = (product: FeedbackProductItem | null | undefined) => (
    String(product?.value ?? product?.id ?? '')
);

const getProductName = (product: FeedbackProductItem | null | undefined) => (
    product?.name || ''
);

export function useFeedbackFormFields(setPost: any, thread?: any) {
    const postData = setPost?.postData || {};

    const setPostData = useCallback((data: Record<string, any>) => {
        setPost?.setPostData?.(data);
    }, [setPost]);

    const [problemLabel, setProblemLabel] = useState(() => String(postData.problemName || ''));
    const [productLabel, setProductLabel] = useState(() => String(postData.productName || ''));
    const solvedCountRequestRef = useRef(0);

    useEffect(() => {
        setProblemLabel(String(postData.problemName || ''));
    }, [postData.problemName, postData.problemId]);

    useEffect(() => {
        setProductLabel(String(postData.productName || ''));
    }, [postData.productName, postData.productId]);

    const fetchSolvedCount = useCallback(async (problemId: string, productId: string) => {
        if (!thread?.getFeedbackSolvedCount) return;
        const requestId = ++solvedCountRequestRef.current;
        setPostData({ problemSolvedCountLoaded: false });
        try {
            const params: Record<string, string> = {};
            if (problemId) params['filter[problemId]'] = problemId;
            if (productId) params['filter[productId]'] = productId;
            const res = await thread.getFeedbackSolvedCount(params);
            if (requestId !== solvedCountRequestRef.current) return;
            const solvedCount = Number(res?.Data?.count ?? res?.data?.count ?? 0) || 0;
            setPostData({ problemSolvedCount: solvedCount, problemSolvedCountLoaded: true });
        } catch (e) {
            if (requestId !== solvedCountRequestRef.current) return;
            setPostData({ problemSolvedCount: 0, problemSolvedCountLoaded: true });
        }
    }, [thread, setPostData]);

    const handleProblemSelect = useCallback((problem: FeedbackProblemItem | null) => {
        const problemId = getProblemId(problem);
        const problemName = getProblemName(problem);
        setProblemLabel(problemName);
        setPostData({
            problemId,
            problemName,
            problemSolvedCount: 0,
            problemSolvedCountLoaded: false,
        });
        fetchSolvedCount(problemId, postData.productId || '');
    }, [setPostData, fetchSolvedCount, postData.productId]);

    const handleProductSelect = useCallback((product: FeedbackProductItem | null) => {
        const productId = getProductId(product);
        const productName = getProductName(product);
        setProductLabel(productName);
        setPostData({
            productId,
            productName,
            problemSolvedCount: 0,
            problemSolvedCountLoaded: false,
        });
        fetchSolvedCount(postData.problemId || '', productId);
    }, [setPostData, fetchSolvedCount, postData.problemId]);

    return {
        postData,
        setPostData,
        problemLabel,
        productLabel,
        handleProblemSelect,
        handleProductSelect,
    };
}
